import {
  createEffect,
  createResource,
  createSignal,
  For,
  Show,
} from "solid-js";
import type {
  AcceptanceRecord,
  CheckResult,
  CommissioningView,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatDateTime, formatNumber, formatShare } from "../format.ts";
import { pathTo, reloadTree, scope, siteTree } from "../context.ts";
import { Icon } from "../icons.tsx";
import { MediumBadge } from "../components/Media.tsx";
import { READINESS_CHIP } from "../components/PointCard.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import {
  Dialog,
  operator,
  PageHeader,
  Skeleton,
  Tabs,
  useAction,
} from "../components/ui.tsx";

const TABS = ["checks", "acceptance", "records"] as const;
type Tab = (typeof TABS)[number];
const STATE_CHIP: Record<string, string> = {
  ok: "ok",
  warn: "warn",
  fail: "crit",
  na: "",
  open: "dash",
};

/** The text behind a check result, with its figures. */
function checkText(c: CheckResult): string {
  const v = c.values;
  const f = (n: number | undefined, d = 1) =>
    formatNumber(n ?? null, locale(), d);
  const p = (n: number | undefined) => formatShare(n ?? null, locale());
  switch (`${c.key}:${c.state}`) {
    case "rhythm:ok":
    case "rhythm:warn":
    case "rhythm:fail":
      return t("commissioning.result.rhythm", {
        share: p(v.share),
        hours: f(v.hours, 0),
      });
    case "clock:ok":
    case "clock:warn":
      return t("commissioning.result.clock", { minutes: f(v.ageMinutes, 0) });
    case "clock:fail":
      return v.aheadMinutes
        ? t("commissioning.result.clockAhead", {
            minutes: f(v.aheadMinutes, 0),
          })
        : t("commissioning.result.clockNone");
    case "scale:ok":
    case "scale:warn":
    case "scale:fail":
      return t("commissioning.result.scale", { difference: p(v.difference) });
    case "direction:ok":
    case "direction:fail":
      return t("commissioning.result.direction", { average: f(v.average, 2) });
    case "counter:ok":
    case "counter:warn":
    case "counter:fail":
      return t("commissioning.result.counter", {
        register: f(v.register, 2),
        integral: f(v.integral, 2),
        difference: p(v.difference),
      });
    default:
      return t(`commissioning.hint.${c.key}`);
  }
}

/**
 * Commissioning and acceptance (prototype "Uruchomienie i odbiór"): a point becomes "running" only after its checks
 * and an acceptance. Points before acceptance raise no alarms; readiness does not follow live connectivity.
 */
export function CommissioningPage() {
  const nodeId = () => scope()?.id;
  const [items, { refetch }] = createResource(nodeId, (id) =>
    api.commissioning.pending(id),
  );
  const [selected, setSelected] = createSignal<string>();
  createEffect(() => {
    const list = items();
    if (list && !list.some((i) => i.point.id === selected())) {
      setSelected(list[0]?.point.id);
    }
  });
  const path = (nodeId: string) =>
    pathTo(siteTree()?.nodes ?? [], nodeId)
      .slice(1)
      .map((n) => n.name)
      .join(" / ");
  return (
    <>
      <PageHeader
        eyebrow={`${t("commissioning.eyebrow")} · ${scope()?.name ?? ""}`}
        title={t("commissioning.title")}
        sub={t("commissioning.intro")}
      />
      <div class="grid g-2 g-2s">
        <section class="card">
          <div class="card-h">
            <h2>{t("commissioning.toCheck")}</h2>
            <span class="meta">
              {t("assets.pointsTotal", { count: items()?.length ?? 0 })}
            </span>
          </div>
          <Show when={!items.error} fallback={<ErrorBox error={items.error} />}>
            <Show
              when={items()}
              fallback={
                <div class="card-b">
                  <Skeleton lines={4} />
                </div>
              }
            >
              <div class="scrollx">
                <table class="tbl">
                  <tbody>
                    <For
                      each={items()}
                      fallback={
                        <tr>
                          <td class="hint">{t("commissioning.allDone")}</td>
                        </tr>
                      }
                    >
                      {(i) => (
                        <tr
                          class={`click ${selected() === i.point.id ? "sel" : ""}`}
                          onClick={() => setSelected(i.point.id)}
                        >
                          <td style={{ width: "24px" }}>
                            <MediumBadge medium={i.point.medium} />
                          </td>
                          <td>
                            {i.point.name}
                            <div class="hint">{path(i.point.nodeId)}</div>
                          </td>
                          <td>
                            <span
                              class={`chip ${READINESS_CHIP[i.point.readiness] ?? ""}`}
                            >
                              {t(`readiness.${i.point.readiness}`)}
                            </span>
                          </td>
                          <td class="r num">
                            {i.done}/{i.total}
                            <Show when={i.failures > 0}>
                              <div>
                                <span class="chip crit">
                                  {t("commissioning.failures", {
                                    count: i.failures,
                                  })}
                                </span>
                              </div>
                            </Show>
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Show>
          </Show>
          <div class="card-b hint">{t("commissioning.listHint")}</div>
        </section>
        <Show
          when={selected()}
          keyed
          fallback={
            <section class="card">
              <div class="card-b hint">{t("commissioning.pick")}</div>
            </section>
          }
        >
          {(id) => (
            <Detail
              pointId={id}
              path={path}
              onChanged={async () => {
                await reloadTree();
                api.invalidate(
                  `/api/v1/nodes/${encodeURIComponent(nodeId()!)}/commissioning`,
                );
                await refetch();
              }}
            />
          )}
        </Show>
      </div>
    </>
  );
}

function Detail(props: {
  pointId: string;
  path: (nodeId: string) => string;
  onChanged: () => Promise<void>;
}) {
  const [tab, setTab] = createSignal<Tab>("checks");
  const [view, { refetch, mutate }] = createResource(
    () => props.pointId,
    (id) => api.commissioning.view(id),
  );
  const [note, setNote] = createSignal("");
  const [protocol, setProtocol] = createSignal<AcceptanceRecord>();
  const [reserving, setReserving] = createSignal<string>();
  const [reservation, setReservation] = createSignal("");
  const [run, error, busy] = useAction();
  const reload = async (next?: CommissioningView) => {
    api.invalidate(
      `/api/v1/points/${encodeURIComponent(props.pointId)}/commissioning`,
    );
    if (next) {
      mutate(next);
    } else {
      await refetch();
    }
    await props.onChanged();
  };
  const setCheck = (
    key: string,
    state: "ok" | "warn" | "na",
    text: string | null,
  ) =>
    void run(async () => {
      await api.commissioning.setCheck(props.pointId, key, state, text);
      await reload();
    });
  const step = (action: "submit" | "accept" | "sendBack") =>
    void run(async () => {
      const next = await api.commissioning[action](
        props.pointId,
        note().trim() || null,
      );
      setNote("");
      await reload(next);
    });

  return (
    <section class="card">
      <Show
        when={view()}
        fallback={
          <div class="card-b">
            <Skeleton lines={6} />
          </div>
        }
      >
        {(v) => {
          const p = () => v().point;
          const open = () =>
            v().checks.filter((c) => c.state === "open" || c.state === "fail");
          const warns = () => v().checks.filter((c) => c.state === "warn");
          const done = () =>
            v().checks.filter((c) => c.state !== "open").length;
          return (
            <>
              <div class="card-h">
                <MediumBadge medium={p().medium} />
                <div style={{ "min-width": 0 }}>
                  <div class="eyebrow">{props.path(p().nodeId)}</div>
                  <h2 style={{ "font-size": "16px" }}>{p().name}</h2>
                </div>
                <span
                  style={{ "margin-left": "auto" }}
                  class={`chip ${READINESS_CHIP[p().readiness] ?? ""}`}
                >
                  {t(`readiness.${p().readiness}`)}
                </span>
              </div>
              <Tabs
                tabs={TABS}
                value={tab()}
                onChange={setTab}
                label={(x) => t(`commissioning.tab.${x}`)}
              />
              <Show when={error()}>
                <div class="card-b">
                  <ErrorBox error={error()} />
                </div>
              </Show>
              <Show when={tab() === "checks"}>
                <div
                  class="card-b"
                  style={{
                    display: "grid",
                    gap: "6px",
                    "padding-bottom": "6px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      "justify-content": "space-between",
                    }}
                    class="hint"
                  >
                    <span>
                      {t("commissioning.progress", {
                        done: done(),
                        total: v().checks.length,
                      })}
                    </span>
                    <span>
                      {t("commissioning.reservations", {
                        count: warns().length,
                      })}
                    </span>
                  </div>
                  <div class="progress">
                    <i
                      style={{
                        width: `${(done() / Math.max(1, v().checks.length)) * 100}%`,
                      }}
                    />
                  </div>
                </div>
                <Show when={!p().binding}>
                  <div class="note">
                    <Icon name="link" />
                    {t("commissioning.noDevice")}
                  </div>
                </Show>
                <div>
                  <For each={v().checks}>
                    {(c) => (
                      <div class="chkrow">
                        <div>
                          <b style={{ "font-weight": 500 }}>
                            {t(`commissioning.check.${c.key}`)}
                          </b>{" "}
                          <span class={`chip ${c.automatic ? "acc" : ""}`}>
                            {c.automatic
                              ? t("commissioning.automatic")
                              : t("commissioning.onSite")}
                          </span>
                          <div class="res">
                            {checkText(c)}
                            {c.note ? ` · „${c.note}”` : ""}
                            {c.by ? ` · ${c.by}` : ""}
                          </div>
                        </div>
                        <Show
                          when={!c.automatic && operator()}
                          fallback={
                            <span class={`chip ${STATE_CHIP[c.state] ?? ""}`}>
                              {t(`commissioning.state.${c.state}`)}
                            </span>
                          }
                        >
                          <div
                            class="tri"
                            role="group"
                            aria-label={t(`commissioning.check.${c.key}`)}
                          >
                            <For each={["ok", "warn", "na"] as const}>
                              {(s) => (
                                <button
                                  type="button"
                                  class={s}
                                  aria-pressed={c.state === s}
                                  disabled={busy()}
                                  onClick={() =>
                                    s === "warn"
                                      ? setReserving(c.key)
                                      : setCheck(c.key, s, null)
                                  }
                                >
                                  {t(`commissioning.state.${s}`)}
                                </button>
                              )}
                            </For>
                          </div>
                        </Show>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
              <Show when={tab() === "acceptance"}>
                <div class="card-b stack">
                  <dl class="kv">
                    <dt>{t("commissioning.scope")}</dt>
                    <dd>{p().name}</dd>
                    <dt>{t("commissioning.unresolved")}</dt>
                    <dd>
                      <Show
                        when={open().length > 0}
                        fallback={
                          <span class="chip ok">{t("commissioning.none")}</span>
                        }
                      >
                        <For each={open()}>
                          {(c) => (
                            <span class="chip crit">
                              {t(`commissioning.check.${c.key}`)}
                            </span>
                          )}
                        </For>
                      </Show>
                    </dd>
                    <dt>{t("commissioning.reservationsLabel")}</dt>
                    <dd>
                      <Show
                        when={warns().length > 0}
                        fallback={t("commissioning.none")}
                      >
                        <For each={warns()}>
                          {(c) => (
                            <span class="chip warn">
                              {t(`commissioning.check.${c.key}`)}
                            </span>
                          )}
                        </For>
                      </Show>
                    </dd>
                  </dl>
                  <div class="field">
                    <label for="acNote">{t("commissioning.note")}</label>
                    <input
                      id="acNote"
                      value={note()}
                      maxLength={2000}
                      onInput={(e) => setNote(e.currentTarget.value)}
                    />
                  </div>
                  <p class="hint" style={{ margin: 0 }}>
                    {t("commissioning.legalHint")}
                  </p>
                  <Show when={operator()}>
                    <div class="row-acts" style={{ "flex-wrap": "wrap" }}>
                      <Show
                        when={
                          p().readiness === "config" ||
                          p().readiness === "project"
                        }
                      >
                        <button
                          type="button"
                          class="btn"
                          disabled={busy() || !p().binding}
                          onClick={() => step("submit")}
                        >
                          {t("commissioning.submit")}
                        </button>
                      </Show>
                      <button
                        type="button"
                        class="btn primary"
                        disabled={busy() || v().outcome === null}
                        onClick={() => step("accept")}
                      >
                        <Icon name="check" />
                        {v().outcome === "acceptedWithReservations"
                          ? t("commissioning.acceptWithReservations")
                          : t("commissioning.accept")}
                      </button>
                      <Show when={p().readiness === "check"}>
                        <button
                          type="button"
                          class="btn"
                          disabled={busy()}
                          onClick={() => step("sendBack")}
                        >
                          {t("commissioning.sendBack")}
                        </button>
                      </Show>
                    </div>
                  </Show>
                </div>
              </Show>
              <Show when={tab() === "records"}>
                <div class="card-b">
                  <Show
                    when={v().records.length > 0}
                    fallback={
                      <p class="hint" style={{ margin: 0 }}>
                        {t("commissioning.noRecords")}
                      </p>
                    }
                  >
                    <ul class="timeline">
                      <For each={v().records}>
                        {(r) => (
                          <li>
                            <time>{formatDateTime(r.at, locale())}</time>
                            <span
                              class={`mk ${r.result === "returned" ? "crit" : r.result === "submitted" ? "acc" : "ok"}`}
                            />
                            <div>
                              <b style={{ "font-weight": 500 }}>
                                {t(`commissioning.result.${r.result}`)}
                              </b>
                              <div class="hint">
                                {r.by}
                                {r.note ? ` · „${r.note}”` : ""}
                              </div>
                              <button
                                type="button"
                                class="btn ghost sm"
                                onClick={() => setProtocol(r)}
                              >
                                <Icon name="file" />
                                {t("commissioning.protocol")}
                              </button>
                            </div>
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                </div>
              </Show>
              <Show when={reserving()}>
                {(key) => (
                  <Dialog
                    title={t("commissioning.reservationTitle", {
                      check: t(`commissioning.check.${key()}`),
                    })}
                    onClose={() => setReserving(undefined)}
                    footer={
                      <button
                        type="button"
                        class="btn primary"
                        disabled={!reservation().trim()}
                        onClick={() => {
                          setCheck(key(), "warn", reservation().trim());
                          setReservation("");
                          setReserving(undefined);
                        }}
                      >
                        {t("common.save")}
                      </button>
                    }
                  >
                    <div class="field">
                      <label for="reservation">
                        {t("commissioning.reservationPrompt")}
                      </label>
                      <textarea
                        id="reservation"
                        rows="3"
                        maxLength={2000}
                        value={reservation()}
                        onInput={(e) => setReservation(e.currentTarget.value)}
                      />
                    </div>
                  </Dialog>
                )}
              </Show>
              <Show when={protocol()}>
                {(r) => (
                  <Protocol
                    record={r()}
                    point={p().name}
                    path={props.path(p().nodeId)}
                    onClose={() => setProtocol(undefined)}
                  />
                )}
              </Show>
            </>
          );
        }}
      </Show>
    </section>
  );
}

/** The acceptance record as a printable protocol. */
function Protocol(props: {
  record: AcceptanceRecord;
  point: string;
  path: string;
  onClose: () => void;
}) {
  return (
    <Dialog
      title={t("commissioning.protocolTitle")}
      onClose={props.onClose}
      wide
      footer={
        <button
          type="button"
          class="btn primary"
          onClick={() => window.print()}
        >
          <Icon name="file" />
          {t("commissioning.print")}
        </button>
      }
    >
      <div class="stack protocol">
        <dl class="kv">
          <dt>{t("assets.point")}</dt>
          <dd>
            {props.point}
            <div class="hint">{props.path}</div>
          </dd>
          <dt>{t("commissioning.resultLabel")}</dt>
          <dd>{t(`commissioning.result.${props.record.result}`)}</dd>
          <dt>{t("commissioning.by")}</dt>
          <dd>{props.record.by}</dd>
          <dt>{t("commissioning.date")}</dt>
          <dd>{formatDateTime(props.record.at, locale())}</dd>
          <Show when={props.record.note}>
            <dt>{t("commissioning.note")}</dt>
            <dd>{props.record.note}</dd>
          </Show>
        </dl>
        <table class="tbl">
          <thead>
            <tr>
              <th>{t("commissioning.checkLabel")}</th>
              <th>{t("commissioning.resultLabel")}</th>
              <th>{t("commissioning.details")}</th>
            </tr>
          </thead>
          <tbody>
            <For each={props.record.checks}>
              {(c) => (
                <tr>
                  <td>{t(`commissioning.check.${c.key}`)}</td>
                  <td>
                    <span class={`chip ${STATE_CHIP[c.state] ?? ""}`}>
                      {t(`commissioning.state.${c.state}`)}
                    </span>
                  </td>
                  <td class="hint">
                    {checkText(c)}
                    {c.note ? ` · „${c.note}”` : ""}
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
        <p class="hint" style={{ margin: 0 }}>
          {t("commissioning.legalHint")}
        </p>
      </div>
    </Dialog>
  );
}
